import { useState } from "react";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { EmptyState } from "@/shared/components/EmptyState";
import { Button } from "@/shared/components/button";
import { PageSpinner } from "@/shared/components/spinner";
import type { HealthRecord, HealthConcern, PatientReportedConcern } from "@/shared/types";
import { HealthOverviewChart } from "@/features/patient/dashboard/components/HealthOverviewChart";
import { RecentVisitsModal } from "@/features/patient/dashboard/components/RecentVisitsModal";
import { defaultDashboardConfig } from "@/features/patient/dashboard/config/dashboard-config";
import { adaptCoverage, type InsuranceSummary } from "@/features/patient/dashboard/utils/adaptInsurance";
import { adaptObservationValue, isAbnormalLabValueStatus, type LabValue, type LabValueStatus } from "@/features/patient/dashboard/utils/adaptLabPanel";
import { adaptTimelineEvent, type TimelineEventData } from "@/features/patient/dashboard/utils/adaptTimelineEvents";
import { RecentUploadsCard } from "@/features/patient/records/components/RecentUploadsCard";
import { useDiagnosticReports } from "@/portals/patient/context/DiagnosticReportsContext";
import { useMedications } from "@/portals/patient/context/MedicationsContext";
import { useVisits } from "@/portals/patient/context/VisitsContext";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";
import { usePatientProfile } from "@/portals/patient/context/PatientProfileContext";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { cn } from "@/shared/lib/utils";

function formatDate(dateString: string): string {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  // Most callers pass a plain "YYYY-MM-DD" date (appending T00:00:00 avoids a
  // timezone-shift showing the wrong day), but some real values (e.g. memberSince)
  // are already a full ISO datetime — appending T00:00:00 to those produces an
  // unparseable double-timestamp string, so only append it when there's no time
  // component already present.
  const date = dateString.includes("T") ? new Date(dateString) : new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "Not available";
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

const FULL_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function formatLongDate(dateString: string): string {
  const date = dateString.includes("T") ? new Date(dateString) : new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "Not available";
  return `${date.getDate()} ${FULL_MONTHS[date.getMonth()]}, ${date.getFullYear()}`;
}

function getInitials(name: string) {
  return name.split(" ").filter(Boolean).slice(0, 2).map(n => n[0]).join("").toUpperCase();
}

interface DashboardProps {
  records: HealthRecord[];
  onRecordSelect: (record: HealthRecord) => void;
  healthConcerns?: HealthConcern[];
  patientConcerns?: PatientReportedConcern[];
  onNavigate?: (view: string) => void;
  // True once at least one real Epic/Athena upload has finished processing —
  // independent of `records` (which only ever gets populated by the separate,
  // local-only PDF/CSV/JSON parsing path, never by a real zip upload). Without
  // this, a patient with real parsed data sitting in the database would still
  // see the empty "upload your records" screen forever.
  hasRealData?: boolean;
  // True until the upload-history fetch that `hasRealData` is derived from has
  // resolved. Without checking this, a patient who already has a completed
  // upload would briefly see the empty state on every login, before flipping
  // to the real dashboard a moment later once the fetch lands.
  isCheckingUploads?: boolean;
}

const uploadTypes = [
  { label: "Lab Reports", description: "Tests, reports, etc.", icon: "lucide:flask-conical" },
  { label: "Prescriptions", description: "Medicines, dosages, etc.", icon: "lucide:pill" },
  { label: "Imaging Reports", description: "X-rays, MRIs, CT-scans, etc.", icon: "lucide:flask-conical" },
  { label: "Discharge summaries", description: "Hospital stays, procedure, etc.", icon: "lucide:flask-conical" },
  { label: "Insurance Documents", description: "Policies cards, documents, etc.", icon: "lucide:file-check-2" },
];

function EmptyDashboard({ onNavigate }: { onNavigate?: (view: string) => void }) {
  return (
    <div className="flex min-h-full flex-col bg-[#F7F7F7] px-8 py-12">
      <div className="mx-auto flex w-full max-w-[1040px] flex-1 flex-col items-center justify-center">
        <img
          src="/dashboardPic.svg"
          alt=""
          className="h-auto w-[310px] max-w-full"
        />

        <h1 className="mt-10 text-center text-[28px] font-extrabold leading-tight text-[#180047]">
          Upload your medical records
        </h1>
        <p className="mt-4 text-center text-[20px] font-medium text-[#7A7F86]">
          To analyze your health and generate meaningful insights
        </p>

        <Button
          onClick={() => onNavigate?.("upload")}
          className="!mt-9 !rounded-lg !bg-[#E4002B]/50 !px-6 !py-3 !text-sm !font-extrabold !text-white hover:!bg-[#E4002B]/60"
        >
          Upload Records
        </Button>

        <div className="mt-12 w-full">
          <h2 className="text-center text-[24px] font-semibold text-[#7A7F86]">What you can upload?</h2>
          <div className="mx-auto mt-8 flex max-w-[880px] flex-wrap justify-center gap-6">
            {uploadTypes.map(({ label, description, icon }) => (
              <div
                key={label}
                className="flex h-[74px] w-[285px] items-center gap-4 rounded-xl border border-[#DADDE5] bg-white px-4 shadow-sm"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E4002B]/50">
                  <Icon icon={icon} className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-extrabold text-[#111827]">{label}</p>
                  <p className="mt-1 truncate text-xs font-medium text-[#7A7F86]">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const statsMeta = [
  { key: "total",     label: "Total Records",     icon: "lucide:file-text" },
  { key: "normal",    label: "Normal Results",     icon: "lucide:check-circle-2" },
  { key: "attention", label: "Needs Attention",    icon: "lucide:alert-circle" },
  { key: "thisMonth", label: "Records This Month", icon: "lucide:calendar" },
];

// ─── Most Recent Lab Result Widget ─────────────────────────────────────────

const LAB_SCORE_THRESHOLDS: [number, string][] = [
  [90, "Excellent"],
  [75, "Good"],
  [50, "Moderate"],
];

function labScoreLabel(score: number): string {
  for (const [threshold, label] of LAB_SCORE_THRESHOLDS) {
    if (score >= threshold) return label;
  }
  return "Needs Attention";
}

function MostRecentLabResultWidget() {
  const { records: parsedRecords, isLoading } = useParsedRecords();

  const labValues = parsedRecords
    .filter((r) => r.resourceType === "Observation")
    .map(adaptObservationValue)
    .filter((v): v is LabValue => v !== null);

  const mostRecentDate = labValues.reduce(
    (latest, v) => (v.date > latest ? v.date : latest),
    ""
  );
  // No reliable Observation -> DiagnosticReport link exists in the pipeline
  // today, so "the panel" is just every value drawn on the most recent date —
  // see adaptLabPanel.ts.
  const panel = mostRecentDate ? labValues.filter((v) => v.date === mostRecentDate) : [];

  if (!isLoading && panel.length === 0) {
    return (
      <div className="flex h-full min-h-[500px] flex-col items-center justify-center rounded-3xl border border-[#E5E7EB] bg-white p-5 text-center shadow-sm">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Most Recent Lab Result</p>
        <EmptyState variant="compact" title="No lab results available yet." className="!py-0 mt-3" />
      </div>
    );
  }

  // isAbnormalLabValueStatus, not a bare `!== "normal"` check — an
  // "unknown" (never assessed) value must count as neither normal NOR
  // abnormal, so it doesn't get flagged here as if it were a real reading.
  const abnormal = panel.filter((v) => isAbnormalLabValueStatus(v.status));
  const score = panel.length > 0 ? Math.round(((panel.length - abnormal.length) / panel.length) * 100) : 0;
  const scoreLabel = labScoreLabel(score);
  const [first, second] = abnormal.length > 0 ? abnormal : panel.slice(0, 2);

  const statPill = (status: LabValueStatus) => (
    <span
      className={cn(
        "mt-1 inline-block w-fit rounded-md px-2 py-0.5 text-[10px] font-semibold capitalize",
        status === "abnormal" ? "bg-[#FEE2E2] text-[#DC2626]" : "bg-[#FEF3C7] text-[#B45309]"
      )}
    >
      {status}
    </span>
  );

  return (
    <div className="flex h-full min-h-[500px] flex-col rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#111827]">Most Recent Lab Result</p>
          {isLoading ? (
            <Skeleton active title={{ width: 120 }} paragraph={false} />
          ) : (
            <h3 className="text-base font-extrabold tracking-tight text-[#111827]">
              {`Recent Labs · ${formatDate(mostRecentDate)}`}
            </h3>
          )}
        </div>
        <Icon icon="lucide:arrow-left" className="h-4 w-4 rotate-[135deg] text-[#9CA3AF]" />
      </div>

      {/* Score */}
      <div className="mb-1 flex items-baseline gap-1.5">
        <span className="text-4xl font-black tracking-tight text-[#111827]">{score}</span>
        <span className="text-lg font-bold text-[#111827]">%</span>
        <span className="ml-1 text-sm font-medium text-[#4B5563]">{scoreLabel}</span>
      </div>

      {/* Progress bar */}
      <div className="relative mb-1 h-2 w-full overflow-hidden rounded-full bg-[#E5E7EB]">
        <div
          className="h-2 rounded-full bg-[#111827] transition-all duration-500"
          style={{ width: `${score}%` }}
        />
      </div>
      <div className="mb-4 flex justify-between text-[10px] text-[#9CA3AF]">
        <span>0</span><span>100%</span>
      </div>

      {/* Central area */}
      <div className="relative flex flex-1 items-center justify-center py-8">
        {/* Left stat */}
        {first && (
          <div className="absolute left-0 top-1/2 flex min-h-[84px] w-[128px] -translate-y-1/2 flex-col justify-center rounded-xl border border-[#E5E7EB] bg-white px-3.5 py-2.5 shadow-md">
            <p className="text-[11px] font-medium text-[#9CA3AF]">{first.label}</p>
            <div className="flex items-baseline gap-0.5">
              <span className="text-2xl font-black text-[#111827]">{first.value}</span>
              {first.unit && <span className="text-xs text-[#4B5563]">{first.unit}</span>}
            </div>
            {first.status !== "normal" && statPill(first.status)}
            {first.rangeLabel && <p className="mt-1 text-[10px] text-[#9CA3AF]">Range: {first.rangeLabel}</p>}
          </div>
        )}

        {/* Center icon */}
        <div
          className="flex h-32 w-32 items-center justify-center rounded-full bg-white"
          style={{ boxShadow: "0 18px 32px rgba(17, 24, 39, 0.16), 0 -12px 30px rgba(17, 24, 39, 0.10), 0 0 0 16px #F4F4F4" }}
        >
          <Icon icon="mdi:heart" className="animate-heartbeat h-16 w-16 text-[#F36F8F]" />
        </div>

        {/* Right stat */}
        {second && (
          <div className="absolute right-0 top-1/2 flex min-h-[84px] w-[128px] -translate-y-1/2 flex-col justify-center rounded-xl border border-[#E5E7EB] bg-white px-3.5 py-2.5 shadow-md">
            <p className="text-[11px] font-medium text-[#9CA3AF]">{second.label}</p>
            <div className="flex items-baseline gap-0.5">
              <span className="text-2xl font-black text-[#111827]">{second.value}</span>
              {second.unit && <span className="text-xs text-[#4B5563]">{second.unit}</span>}
            </div>
            {second.status !== "normal" && statPill(second.status)}
            {second.rangeLabel && <p className="mt-1 text-[10px] text-[#9CA3AF]">Range: {second.rangeLabel}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Upcoming Appointments ──────────────────────────────────────────────────

const calendarDays = [
  { day: 18, label: "Sun" },
  { day: 19, label: "Mon" },
  { day: 20, label: "Tue" },
  { day: 21, label: "Wed", active: true },
  { day: 22, label: "Thu" },
  { day: 23, label: "Fri" },
  { day: 24, label: "Sat" },
];

function AppointmentsWidget({ onNavigate }: { onNavigate?: (v: string) => void }) {
  const [showVisitsModal, setShowVisitsModal] = useState(false);
  const { visits, isLoading } = useVisits();
  // Already sorted most-recent-first by VisitsContext.
  const featured = visits[0];
  const now = new Date();

  return (
    <>
      <div
        onClick={() => setShowVisitsModal(true)}
        className="cursor-pointer rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm transition-colors hover:border-[#F36F8F]/40"
      >
        <div className="mb-3 flex items-start justify-between">
          <h3 className="text-base font-extrabold tracking-tight text-[#111827]">Recent Visits</h3>
          <Icon icon="lucide:arrow-left" className="h-4 w-4 rotate-[135deg] text-[#9CA3AF]" />
        </div>

        {/* Date strip */}
        <div className="mb-4 flex items-center gap-1">
          <Button
            variant="outline"
            onClick={(e) => e.stopPropagation()}
            className="!flex !h-9 !w-9 !shrink-0 !items-center !justify-center !rounded-xl !border-[#E5E7EB] !bg-white !p-0 hover:!border-[#E5E7EB] hover:!bg-[#F3F4F6]"
          >
            <Icon icon="lucide:chevron-left" className="h-4 w-4 text-[#4B5563]" />
          </Button>
          <div className="flex flex-1 justify-between">
            {calendarDays.map((d) => (
              <div
                key={d.day}
                onClick={(e) => e.stopPropagation()}
                className={cn(
                  "flex w-9 flex-col items-center gap-0.5 rounded-xl py-1.5 text-xs font-semibold transition-colors",
                  d.active
                    ? "bg-[#F36F8F] text-white"
                    : "cursor-pointer bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]"
                )}
              >
                <span className={cn("text-sm font-bold", d.active ? "text-white" : "text-[#111827]")}>{d.day}</span>
                <span className={cn("text-[10px]", d.active ? "text-white/80" : "text-[#9CA3AF]")}>{d.label}</span>
              </div>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={(e) => e.stopPropagation()}
            className="!flex !h-9 !w-9 !shrink-0 !items-center !justify-center !rounded-xl !border-[#E5E7EB] !bg-white !p-0 hover:!border-[#E5E7EB] hover:!bg-[#F3F4F6]"
          >
            <Icon icon="lucide:chevron-right" className="h-4 w-4 text-[#4B5563]" />
          </Button>
        </div>

        {/* Doctor card */}
        <div className="flex items-center gap-3 rounded-xl bg-[#F3F4F6] px-4 py-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#9187A8] text-xs font-bold text-white">
            {isLoading ? "…" : featured?.initials ?? "–"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-[#111827]">
              {isLoading ? "Loading…" : featured?.doctor ?? "No visits found yet"}
            </p>
            {!isLoading && featured?.specialty && (
              <p className="text-xs text-[#4B5563]">{featured.specialty}</p>
            )}
            {!isLoading && featured?.date && (
              <p className="text-xs text-[#9CA3AF]">{formatDate(featured.date)}</p>
            )}
          </div>
        </div>
      </div>

      {showVisitsModal && (
        <RecentVisitsModal
          visits={visits}
          initialMonth={featured ? new Date(featured.date + "T00:00:00").getMonth() : now.getMonth()}
          initialYear={featured ? new Date(featured.date + "T00:00:00").getFullYear() : now.getFullYear()}
          onClose={() => setShowVisitsModal(false)}
        />
      )}
    </>
  );
}

// ─── Current Medications ────────────────────────────────────────────────────

const MEDICATIONS_WIDGET_LIMIT = 3;

function MedicationsWidget({ onNavigate }: { onNavigate?: (v: string) => void }) {
  const { medications, isLoading } = useMedications();
  const activeMeds = medications.filter((m) => m.status === "active").slice(0, MEDICATIONS_WIDGET_LIMIT);

  return (
    <div className="rounded-3xl border border-[#E5E7EB] bg-white px-5 py-5 shadow-sm">
      <div className="mb-7 flex items-center justify-between">
        <h3 className="text-base font-extrabold tracking-tight text-[#111827]">Current Medications</h3>
        <Button
          variant="link"
          onClick={() => onNavigate?.("medications")}
          className="!h-auto !p-0 !text-xs !font-medium !text-[#4B5563] hover:!text-[#111827]"
        >
          See all
        </Button>
      </div>
      <div className="space-y-0">
        {isLoading ? (
          [0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 py-2.5">
              <Skeleton.Avatar active size={36} shape="circle" />
              <div className="flex-1">
                <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
              </div>
            </div>
          ))
        ) : activeMeds.length === 0 ? (
          <EmptyState variant="compact" title="No active medications found." className="!py-2.5 !text-xs" />
        ) : (
          activeMeds.map((med, index) => (
            <div key={med.id}>
              <div className="flex items-center gap-3 py-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F36F8F]">
                  <Icon icon="lucide:link-2" className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#111827]">{med.name}</p>
                  <p className="text-xs text-[#4B5563]">{med.frequency}</p>
                </div>
                <span className="text-sm font-semibold text-[#111827]">{med.dosage}</span>
              </div>
              {index < activeMeds.length - 1 && <div className="h-px bg-[#F3F4F6]" />}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Recent Labs ────────────────────────────────────────────────────────────

const RECENT_LABS_WIDGET_LIMIT = 3;

function RecentLabsWidget({ onNavigate }: { onNavigate?: (v: string) => void }) {
  const { recentLabs, isLoading } = useDiagnosticReports();
  const items = recentLabs.slice(0, RECENT_LABS_WIDGET_LIMIT);

  return (
    <div className="rounded-3xl border border-[#E5E7EB] bg-white px-5 py-5 shadow-sm">
      <div className="mb-7 flex items-center justify-between">
        <h3 className="text-base font-extrabold tracking-tight text-[#111827]">Recent Labs</h3>
        <Button
          variant="link"
          onClick={() => onNavigate?.("diagnostics")}
          className="!h-auto !p-0 !text-xs !font-medium !text-[#4B5563] hover:!text-[#111827]"
        >
          View all
        </Button>
      </div>
      <div className="space-y-0">
        {isLoading ? (
          [0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 py-2.5">
              <Skeleton.Avatar active size={36} shape="circle" />
              <div className="flex-1">
                <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
              </div>
            </div>
          ))
        ) : items.length === 0 ? (
          <EmptyState variant="compact" title="No lab results found yet." className="!py-2.5 !text-xs" />
        ) : (
          items.map((lab, index) => (
            <div key={lab.id}>
              <div className="flex items-center gap-3 py-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F36F8F]">
                  <Icon icon="lucide:file-text" className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#111827]">{lab.name}</p>
                  <p className="text-xs text-[#4B5563]">{lab.date} · {lab.type}</p>
                </div>
                <Icon icon="lucide:chevron-right" className="h-4 w-4 shrink-0 text-[#9CA3AF]" />
              </div>
              {index < items.length - 1 && <div className="h-px bg-[#F3F4F6]" />}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Care Timeline ──────────────────────────────────────────────────────────

const CARE_TIMELINE_LIMIT = 6;

const TIMELINE_ICONS: Record<string, string> = {
  Encounter: "lucide:calendar",
  MedicationRequest: "lucide:pill",
  Procedure: "lucide:stethoscope",
  Immunization: "lucide:syringe",
  DiagnosticReport: "lucide:file-text",
  Condition: "lucide:alert-circle",
};

function CareTimelineWidget({ onNavigate }: { onNavigate?: (v: string) => void }) {
  const { records: parsedRecords, isLoading } = useParsedRecords();
  const { profile } = usePatientProfile();
  // Events already come from the correctly-scoped ParsedRecordsContext — but the
  // name/avatar shown above them must follow the same target, or a dependent's
  // events render under the real logged-in patient's own name.
  const { activeDependent } = useFamily();

  const events = parsedRecords
    .map(adaptTimelineEvent)
    .filter((e): e is TimelineEventData => e !== null)
    .sort((a, b) => a.date.localeCompare(b.date));
  const visibleEvents = events.slice(-CARE_TIMELINE_LIMIT);

  const patientName = activeDependent ? activeDependent.name : `${profile.firstName} ${profile.lastName}`.trim() || "Patient";
  const initials = getInitials(patientName) || "?";
  // memberSince tracks the real account's signup date — a dependent profile has
  // no equivalent, so it's simply omitted rather than showing the wrong date.
  const memberSince = !activeDependent && profile.memberSince ? formatDate(profile.memberSince) : null;

  return (
    <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-extrabold tracking-tight text-[#111827]">
          {activeDependent ? `${activeDependent.name}'s Health Timeline` : "Your Health Timeline"}
        </h3>
        <Button
          variant="link"
          onClick={() => onNavigate?.("timeline")}
          className="!h-auto !p-0 !text-xs !font-extrabold !text-[#111827] hover:!text-[#E4002B]"
        >
          View All
        </Button>
      </div>

      {/* Patient row */}
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#9187A8] text-xs font-extrabold text-white">
          {initials}
        </div>
        <div>
          <p className="text-sm font-extrabold leading-tight text-[#111827]">{patientName}</p>
          {isLoading ? (
            <div className="mt-1.5">
              <Skeleton active title={{ width: 100 }} paragraph={false} />
            </div>
          ) : (
            <p className="mt-1 text-xs leading-tight text-[#4B5563]">
              {memberSince ? `Patient since ${memberSince} · ` : ""}
              {events.length} event{events.length === 1 ? "" : "s"}
            </p>
          )}
        </div>
      </div>

      {/* Timeline — while parsedRecords is still loading, events defaults to
          empty just like the real "nothing shared" case; without this branch
          the widget flashes "0 events" / the empty state before the real
          count and timeline replace it a moment later. */}
      {isLoading ? (
        <div className="flex items-start justify-between px-1 pb-1">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex w-[82px] flex-col items-center">
              <Skeleton.Avatar active size={36} shape="circle" />
              <div className="mt-3 w-full">
                <Skeleton active title={{ width: "80%" }} paragraph={false} />
              </div>
            </div>
          ))}
        </div>
      ) : visibleEvents.length === 0 ? (
        <EmptyState variant="compact" title="No health events recorded yet." className="!py-6" />
      ) : (
        <div className="relative px-1 pb-1">
          {/* Connecting line */}
          <div className="absolute left-6 right-6 top-[18px] h-px bg-[#D1D5DB]" />

          <div className="flex items-start justify-between">
            {visibleEvents.map((event, index) => {
              const iconName = TIMELINE_ICONS[event.resourceType] ?? "lucide:calendar";
              const isLast = index === visibleEvents.length - 1;
              return (
                <div key={`${event.resourceType}-${event.date}-${index}`} className="flex w-[82px] flex-col items-center">
                  <div className={cn(
                    "relative z-10 flex h-9 w-9 items-center justify-center rounded-full border-2",
                    isLast ? "border-[#F36F8F] bg-white" : "border-[#D1D5DB] bg-[#F3F4F6]"
                  )}>
                    <Icon icon={iconName} className={cn("h-4 w-4", isLast ? "text-[#F36F8F]" : "text-[#6B7280]")} />
                  </div>
                  <p className="mt-3 line-clamp-2 text-center text-[10px] font-extrabold leading-tight text-[#111827]">{event.label}</p>
                  <p className="mt-1 text-center text-[10px] leading-tight text-[#9CA3AF]">{formatDate(event.date)}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Insurance Summary ──────────────────────────────────────────────────────

function InsuranceSummaryWidget({
  coverage,
  isLoading,
  onNavigate,
}: {
  coverage?: InsuranceSummary;
  isLoading: boolean;
  onNavigate?: (v: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-3xl border border-[#E5E7EB] shadow-sm">
      {/* Dark header */}
      <div className="bg-[#180047] px-5 py-4">
        <div className="mb-2 flex items-center gap-2">
          <Icon icon="lucide:shield" className="h-4 w-4 text-white" />
          <p className="text-xs font-medium text-white/70">Insurance Summary</p>
        </div>
        {isLoading ? (
          <Skeleton
            active
            title={{ width: 140 }}
            paragraph={false}
            className="[&_.ant-skeleton-title]:!bg-[linear-gradient(90deg,rgba(255,255,255,0.2)_25%,rgba(255,255,255,0.4)_37%,rgba(255,255,255,0.2)_63%)]"
          />
        ) : (
          <h3 className="text-2xl font-extrabold tracking-tight text-white">
            {coverage?.name ?? "No insurance on file"}
          </h3>
        )}
        <p className="mt-1 text-xs text-white/60">
          {!isLoading && coverage && `${coverage.planType} · Member #${coverage.memberId}`}
        </p>
      </div>

      {/* Body */}
      <div className="space-y-3 bg-white px-5 py-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#6B7280]">Start Date</p>
          <p className="text-sm font-extrabold text-[#111827]">
            {coverage?.startDate ? formatLongDate(coverage.startDate) : "—"}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#6B7280]">End Date</p>
          <p className="text-sm font-extrabold text-[#111827]">
            {coverage?.endDate ? formatLongDate(coverage.endDate) : "—"}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#6B7280]">Status</p>
          <p className="flex items-center gap-1.5 text-sm font-extrabold text-[#111827]">
            <span className={cn("h-2 w-2 rounded-full", coverage?.isActive ? "bg-[#22C55E]" : "bg-[#9CA3AF]")} />
            {coverage ? (coverage.isActive ? "Active" : "Inactive") : "—"}
          </p>
        </div>

        {/* CTA */}
        <Button
          variant="outline"
          onClick={() => onNavigate?.("insurance-cards")}
          className="!mt-2 !h-auto !w-full !rounded-full !border-[#180047] !py-2.5 !text-sm !font-extrabold !text-[#180047] hover:!border-[#180047] hover:!bg-[#F3F4F6] hover:!text-[#180047] hover:!shadow-sm"
        >
          View Full Coverage Details
        </Button>
      </div>
    </div>
  );
}

// ─── Main Dashboard ─────────────────────────────────────────────────────────

export function Dashboard({
  records,
  onRecordSelect,
  healthConcerns = [],
  patientConcerns = [],
  onNavigate,
  hasRealData = false,
  isCheckingUploads = false,
}: DashboardProps) {
  const dashboardConfig = defaultDashboardConfig;

  const isWidgetEnabled = (widgetId: string) => dashboardConfig.enabledWidgets.includes(widgetId);

  const { records: parsedRecords, isLoading: isParsedRecordsLoading } = useParsedRecords();
  const coverageRecords = parsedRecords.filter(r => r.resourceType === "Coverage").map(adaptCoverage);
  const activeCoverage = coverageRecords.find(c => c.isActive) ?? coverageRecords[0];
  const attentionParsedRecords = parsedRecords.filter(r => r.needsReview);
  const normalParsedRecords    = parsedRecords.filter(r => !r.needsReview);
  const thisMonthParsedRecords = parsedRecords.filter(r => {
    const d = new Date(r.createdAt);
    const n = new Date();
    return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
  });

  const stats = {
    total:     parsedRecords.length,
    normal:    normalParsedRecords.length,
    attention: attentionParsedRecords.length,
    thisMonth: thisMonthParsedRecords.length,
  };

  const enabledStatsCount = statsMeta.filter(s => isWidgetEnabled(`stats-${s.key}`)).length;

  // Don't decide "no data" until we've actually checked — `hasRealData` reads
  // as false by default before the upload-history fetch resolves, which would
  // otherwise flash the empty state for a patient who already has real,
  // completed uploads.
  if (isCheckingUploads) {
    return <PageSpinner className="bg-[#F7F7F7]" />;
  }

  if (records.length === 0 && !hasRealData) {
    return <EmptyDashboard onNavigate={onNavigate} />;
  }

  return (
    <div className="min-h-full bg-[#F7F7F7] px-2 pt-2 pb-2 lg:px-4 lg:pt-3 lg:pb-4">

      {/* Stats Cards */}
      {enabledStatsCount > 0 && (
        <div className={cn(
          "mb-6 grid gap-3",
          enabledStatsCount === 1 && "max-w-xs grid-cols-1",
          enabledStatsCount === 2 && "sm:grid-cols-2",
          enabledStatsCount === 3 && "sm:grid-cols-2 lg:grid-cols-3",
            enabledStatsCount >= 4 && "grid-cols-2 xl:grid-cols-4"
        )}>
          {statsMeta.map(({ key, label }) => {
            if (!isWidgetEnabled(`stats-${key}`)) return null;
            const value = stats[key as keyof typeof stats];
            return (
              <div
                key={key}
                className="rounded-3xl border border-[#E5E7EB] bg-white px-6 py-5 shadow-sm flex flex-col justify-between min-h-[110px]"
              >
                <p className="text-sm font-normal text-[#9CA3AF]">{label}</p>
                <div className="flex justify-end">
                  <p className="text-3xl font-bold text-[#111827]">{value}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Row 1 — Organ Widget + Health Overview */}
      <div className="grid gap-5 lg:grid-cols-2">
        {isWidgetEnabled("health-concerns") && <MostRecentLabResultWidget />}

        {isWidgetEnabled("health-overview") && (
          <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-extrabold tracking-tight text-[#111827]">Health Overview</h3>
            </div>
            <HealthOverviewChart records={parsedRecords} />
          </div>
        )}
      </div>

      {/* Row 2 — Appointments · Medications · Recent Labs (3 equal cols) */}
      <div className="mt-5 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {isWidgetEnabled("upcoming-appointments") && (
          <AppointmentsWidget onNavigate={onNavigate} />
        )}
        {isWidgetEnabled("medications-summary") && (
          <MedicationsWidget onNavigate={onNavigate} />
        )}
        {isWidgetEnabled("lab-results-summary") && (
          <RecentLabsWidget onNavigate={onNavigate} />
        )}
      </div>

      {/* Row 3 — Care Timeline (2/3) + Insurance Summary (1/3) */}
      <div className="mt-5 grid gap-5 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <CareTimelineWidget onNavigate={onNavigate} />
          {/* Same component as the Upload Records page's "Recent Uploads"
              card — reused as-is (including its own redirect into Document
              Panel) rather than a lookalike copy, so the two can never drift
              out of sync. Stacked under Health Timeline, in its own column
              only — every other row on this dashboard is untouched. */}
          <div className="mt-5">
            <RecentUploadsCard />
          </div>
        </div>
        <div className="lg:col-span-1">
          <InsuranceSummaryWidget coverage={activeCoverage} isLoading={isParsedRecordsLoading} onNavigate={onNavigate} />
        </div>
      </div>
    </div>
  );
}
